import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SendNewOfferForm from "components/SendNewOfferForm";
import { getAuctionBids, getAuctionStatus } from "../../api/auction";

const formatIls = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return "-";
  return `₪${amount.toLocaleString("he-IL", { maximumFractionDigits: 0 })}`;
};

const formatBidTime = (value) => {
  if (!value) return "";
  const normalized = typeof value === "string" ? value.replace(" ", "T") : value;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("he-IL", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const resolveCurrentUserId = (currentUser, offer) => {
  const id = Number(
    currentUser?.id ??
      currentUser?.user_id ??
      currentUser?.userId ??
      offer?.user_id ??
      offer?.userId
  );

  return Number.isFinite(id) && id > 0 ? id : null;
};

const normalizeBidsPayload = (payload, statusPayload = null) => {
  const bids = Array.isArray(payload?.bids) ? payload.bids : [];
  const totalBids = Number(payload?.summary?.total_bids ?? bids.length) || 0;
  const highestBid =
    payload?.summary?.highest_bid ??
    statusPayload?.highest_bid ??
    statusPayload?.highest_offer ??
    bids[0]?.bid_amount ??
    null;
  const highlightedBid = bids.find((bid) => bid?.is_current_user);
  const currentUserPosition =
    Number(payload?.summary?.current_user_position ?? highlightedBid?.position) || null;
  const currentUserBid = payload?.summary?.current_user_bid ?? highlightedBid?.bid_amount ?? null;

  return {
    bids,
    totalBids,
    highestBid,
    currentUserPosition,
    currentUserBid,
  };
};

const resolveDealerName = (bid, allowRealNames = true) => {
  if (!allowRealNames) return "";

  const name =
    bid?.dealer_name ??
    bid?.dealer_full_name ??
    bid?.dealer_business_name ??
    bid?.business_name ??
    bid?.contact_name ??
    bid?.full_name ??
    bid?.name ??
    bid?.bidder_name;

  return String(name ?? "").trim();
};

const isActiveAuctionPayload = (payload) => {
  if (!payload) return false;

  const status = String(payload?.status ?? payload?.auction_status ?? "").toLowerCase();
  const isAuction = Number(payload?.is_auction ?? 0) === 1 || payload?.is_auction === true;
  const statusIsActive = status === "active" || status === "live";

  return isAuction && statusIsActive && payload?.has_started === true && payload?.has_ended !== true;
};

function AuctionMetric({ label, value, tone = "neutral" }) {
  const toneClasses = {
    strong: "border-[#111827] bg-[#111827] text-white shadow-[0_14px_30px_rgba(17,24,39,0.18)]",
    accent: "border-[#D9E8FF] bg-[#F3F8FF] text-[#174A8B]",
    neutral: "border-[#E7EAF0] bg-white text-[#111827]",
  };

  return (
    <div className={`auction-watch-metric min-w-0 rounded-[14px] border px-2 py-2.5 text-center ${toneClasses[tone]}`} dir="rtl">
      <div className={`auction-watch-metric-label font-semibold ${tone === "strong" ? "text-white/75" : "text-[#64748B]"}`}>
        {label}
      </div>
      <div className={`auction-watch-metric-value mt-1 font-bold leading-tight tracking-normal ${tone === "strong" ? "text-white" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function AuctionBidsView({ state, loading, error, showCurrentUserLabel = true }) {
  const hasBids = state.bids.length > 0;
  const positionText = state.currentUserPosition
    ? `${state.currentUserPosition} מתוך ${state.totalBids}`
    : "-";
  const canShowRealNames = !showCurrentUserLabel;
  const leaderText = hasBids ? resolveDealerName(state.bids[0], canShowRealNames) || "סוחר" : "אין הצעות";
  const thirdMetricLabel = showCurrentUserLabel ? "המיקום שלך" : "סוחר מוביל";
  const thirdMetricValue = showCurrentUserLabel ? positionText : leaderText;

  return (
    <div className="flex flex-col gap-4 px-4 pb-5 pt-1" dir="rtl">
      <div className="auction-watch-metrics grid grid-cols-3 gap-2.5">
        <AuctionMetric label="הצעה גבוהה" value={formatIls(state.highestBid)} tone="accent" />
        <AuctionMetric label='סה"כ הצעות' value={state.totalBids.toLocaleString("he-IL")} tone="accent" />
        <AuctionMetric label={thirdMetricLabel} value={thirdMetricValue} />
      </div>

      {loading && (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-[74px] animate-pulse rounded-[18px] bg-[#EEF2F7]" />
          ))}
        </div>
      )}

      {!loading && error && (
        <div className="rounded-[18px] border border-[#F7CACA] bg-[#FFF5F5] p-4 text-center text-[14px] font-medium text-[#B42318]">
          {error}
        </div>
      )}

      {!loading && !error && state.bids.length === 0 && (
        <div className="rounded-[20px] border border-dashed border-[#D6DAE2] bg-white p-6 text-center text-[15px] font-medium text-[#6B7280]">
          עדיין לא הוגשו הצעות במכרז
        </div>
      )}

      {!loading && !error && state.bids.length > 0 && (
        <div className="rounded-[20px] border border-[#E4E9F2] bg-white p-3 shadow-[0_10px_26px_rgba(15,23,42,0.07)]">
          <div className="mb-3 flex items-center justify-between border-b border-[#EEF2F7] pb-2">
            <div className="auction-bids-board-title text-[15px] font-bold text-[#111827]">לוח הצעות</div>
            <div className="auction-bids-board-subtitle text-[12px] font-semibold text-[#64748B]">מהגבוה לנמוך</div>
          </div>
          <div className="auction-bids-scroll-list flex max-h-[36vh] flex-col gap-2 overflow-y-auto overscroll-contain pr-1 md:max-h-[42vh]">
            {state.bids.map((bid) => {
              const isCurrentUser = Boolean(bid?.is_current_user);
              const dealerName = resolveDealerName(bid, canShowRealNames);
              const bidderLabel = showCurrentUserLabel
                ? bid?.bidder_label || (isCurrentUser ? "ההצעה שלך" : `מציע #${bid.position}`)
                : dealerName || "סוחר";
              return (
                <div
                  key={bid.id}
                  className={`rounded-[16px] border p-3 transition ${
                    isCurrentUser
                      ? "border-[#CE0000] bg-[#FFF4F4] shadow-[0_12px_26px_rgba(206,0,0,0.13)]"
                      : "border-[#E7EAF0] bg-[#FBFCFE]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <div
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[14px] font-bold ${
                          isCurrentUser ? "bg-[#CE0000] text-white" : "bg-[#EEF2F7] text-[#334155]"
                        }`}
                      >
                        {bid.position}
                      </div>
                      <div className="min-w-0">
                        <div className="auction-bid-title-row">
                          <div className="auction-bidder-label text-[15px] font-bold text-[#111827]">
                            {showCurrentUserLabel && isCurrentUser ? "ההצעה שלך" : bidderLabel}
                          </div>
                        </div>
                        <div className="auction-bid-time text-[12px] text-[#6B7280]">{formatBidTime(bid.created_at)}</div>
                      </div>
                    </div>
                    <div className="auction-bid-amount shrink-0 text-left text-[18px] font-bold text-[#111827]" dir="ltr">
                      {formatIls(bid.bid_amount)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LeftSendOffer({
  isRejectedOrApproved,
  onClose,
  car,
  currentUser,
  offer,
  onUpdateOffer,
  viewOnlyAuction = false,
}) {
  const [activeTab, setActiveTab] = useState(viewOnlyAuction ? "auction" : "offer");
  const [refreshKey, setRefreshKey] = useState(0);
  const [isActiveAuction, setIsActiveAuction] = useState(false);
  const [auctionBidsState, setAuctionBidsState] = useState({
    bids: [],
    highestBid: null,
    totalBids: 0,
    currentUserPosition: null,
    currentUserBid: null,
  });
  const [auctionBidsLoading, setAuctionBidsLoading] = useState(false);
  const [auctionBidsRefreshing, setAuctionBidsRefreshing] = useState(false);
  const [auctionBidsError, setAuctionBidsError] = useState("");
  const [auctionStatusSnapshot, setAuctionStatusSnapshot] = useState(null);
  const [auctionRedirecting, setAuctionRedirecting] = useState(false);
  const redirectTimerRef = useRef(null);

  const carId = useMemo(() => {
    const id = Number(car?.id ?? car?.car_id);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [car?.car_id, car?.id]);

  const currentUserId = useMemo(
    () => resolveCurrentUserId(currentUser, offer),
    [currentUser, offer]
  );

  const shouldCheckAuction = useMemo(() => {
    const isAuction = Number(car?.is_auction ?? car?.isAuction ?? 0) === 1 || car?.is_auction === true;
    return Boolean(carId && isAuction);
  }, [car?.isAuction, car?.is_auction, carId]);

  const handleUpdateOffer = useCallback(
    (...args) => {
      if (typeof onUpdateOffer === "function") {
        onUpdateOffer(...args);
      }
      setRefreshKey((key) => key + 1);
    },
    [onUpdateOffer]
  );

  const handleAuctionOfferSubmitted = useCallback(() => {
    setRefreshKey((key) => key + 1);
    setAuctionRedirecting(true);

    if (redirectTimerRef.current) {
      window.clearTimeout(redirectTimerRef.current);
    }

    redirectTimerRef.current = window.setTimeout(() => {
      setActiveTab("auction");
      setAuctionRedirecting(false);
      setRefreshKey((key) => key + 1);
    }, 3000);
  }, []);

  useEffect(() => {
    if (!shouldCheckAuction) {
      setIsActiveAuction(false);
      setActiveTab(viewOnlyAuction ? "auction" : "offer");
      setAuctionBidsState({
        bids: [],
        highestBid: null,
        totalBids: 0,
        currentUserPosition: null,
        currentUserBid: null,
      });
      setAuctionBidsError("");
      setAuctionStatusSnapshot(null);
      return undefined;
    }

    let cancelled = false;
    let didInitialLoad = false;

    const fetchAuctionSnapshot = async () => {
      const silent = didInitialLoad;
      if (silent) {
        setAuctionBidsRefreshing(true);
      } else {
        setAuctionBidsLoading(true);
      }
      setAuctionBidsError("");

      const [statusResult, bidsResult] = await Promise.all([
        getAuctionStatus(carId, currentUserId),
        getAuctionBids(carId, currentUserId),
      ]);
      if (cancelled) return;

      const active = statusResult.success && isActiveAuctionPayload(statusResult.data);
      setAuctionStatusSnapshot(statusResult.success ? statusResult.data : null);
      setIsActiveAuction(active);
      if (!active) {
        setActiveTab(viewOnlyAuction ? "auction" : "offer");
      } else if (viewOnlyAuction) {
        setActiveTab("auction");
      }

      if (bidsResult.success && bidsResult.data?.success !== false) {
        setAuctionBidsState(normalizeBidsPayload(bidsResult.data, statusResult.data));
      } else {
        setAuctionBidsError(bidsResult.error || bidsResult.data?.error || "שגיאה בטעינת הצעות המכרז");
      }

      didInitialLoad = true;
      setAuctionBidsLoading(false);
      setAuctionBidsRefreshing(false);
    };

    fetchAuctionSnapshot();
    const intervalId = window.setInterval(fetchAuctionSnapshot, 7000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [carId, currentUserId, refreshKey, shouldCheckAuction, viewOnlyAuction]);

  useEffect(() => () => {
    if (redirectTimerRef.current) {
      window.clearTimeout(redirectTimerRef.current);
    }
  }, []);

  const commonFormProps = {
    variant: "panel",
    isRejectedOrApproved,
    onClose,
    car,
    currentUser,
    offer,
    auctionStatusOverride: auctionStatusSnapshot,
    onAuctionOfferSubmitted: handleAuctionOfferSubmitted,
  };

  if (!isActiveAuction && !viewOnlyAuction) {
    return (
      <div
        className="my-ads-dealer left-send-offer flex w-[19%] flex-col gap-3 rounded-[24px] bg-secondary-primary_white shadow-xs md:w-full sm:p-4 overflow-y-hidden overflow-x-hidden"
        style={{
          position: "fixed",
          top: "1rem",
          bottom: "1rem",
          left: "2rem",
          boxSizing: "border-box",
        }}
      >
        <style>
          {`
            .left-send-offer {
              overflow-y: hidden !important;
              overflow-x: hidden !important;
            }

            .left-send-offer-content {
              flex: 1 1 auto;
              min-height: 0 !important;
              overflow-y: auto !important;
              overflow-x: hidden !important;
              -webkit-overflow-scrolling: touch;
            }
          `}
        </style>
        <div className="left-send-offer-content w-full flex justify-center">
          <div className="w-full">
            <SendNewOfferForm
              {...commonFormProps}
              onUpdateOffer={onUpdateOffer}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="my-ads-dealer left-send-offer left-send-offer-auction-shell flex w-[19%] flex-col gap-3 rounded-[24px] bg-[#F8FAFC] shadow-[0_18px_45px_rgba(15,23,42,0.14)] md:w-full sm:p-4 overflow-y-hidden overflow-x-hidden"
      style={{
        position: "fixed",
        top: "1rem",
        bottom: "1rem",
        left: "2rem",
        boxSizing: "border-box",
      }}
    >
      <style>
        {`
          .left-send-offer-auction-shell {
            overflow-y: hidden !important;
            overflow-x: hidden !important;
          }

          .left-send-offer-auction-content {
            flex: 1 1 auto;
            min-height: 0 !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
            -webkit-overflow-scrolling: touch;
          }

          .left-send-offer-auction-shell .auction-watch-metrics {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
          }

          .left-send-offer-auction-shell .auction-bid-title-row {
            display: grid;
            grid-template-columns: minmax(0, 1fr) max-content;
            align-items: center;
            max-width: 100%;
          }

          .left-send-offer-auction-shell .auction-watch-metric-label,
          .left-send-offer-auction-shell .auction-watch-metric-value,
          .left-send-offer-auction-shell .auction-bidder-label,
          .left-send-offer-auction-shell .auction-bid-current-tag {
            min-width: 0;
            max-width: 100%;
            white-space: nowrap;
            overflow: visible;
            text-overflow: clip;
            overflow-wrap: normal;
            word-break: keep-all;
          }

          .left-send-offer-auction-shell .auction-watch-metric-label {
            font-size: clamp(7px, 0.68vw, 11px);
            line-height: 1.2;
          }

          .left-send-offer-auction-shell .auction-watch-metric-value {
            font-size: clamp(9px, 0.86vw, 15px);
            line-height: 1.15;
          }

          .left-send-offer-auction-shell .auction-bidder-label {
            font-size: clamp(9px, 0.86vw, 15px);
            line-height: 1.2;
            overflow: hidden;
            text-align: right;
          }

          .left-send-offer-auction-shell .auction-bid-current-tag {
            font-size: clamp(7px, 0.62vw, 11px);
            line-height: 1.15;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            transform: none;
          }

          @media (min-width: 769px) and (max-width: 1440px) {
            .left-send-offer-auction-shell .auction-watch-metrics {
              gap: 0.25rem;
            }

            .left-send-offer-auction-shell .auction-watch-metric {
              padding: 0.5rem 0.25rem;
            }

            .left-send-offer-auction-shell .auction-watch-metric-label {
              font-size: clamp(7px, 0.62vw, 9px);
              line-height: 1.15;
            }

            .left-send-offer-auction-shell .auction-watch-metric-value {
              font-size: clamp(8px, 0.78vw, 12px);
              line-height: 1.15;
            }

            .left-send-offer-auction-shell .auction-bids-board-title {
              font-size: 13px;
            }

            .left-send-offer-auction-shell .auction-bids-board-subtitle,
            .left-send-offer-auction-shell .auction-bid-time {
              font-size: 10px;
            }

            .left-send-offer-auction-shell .auction-bidder-label {
              font-size: clamp(8px, 0.78vw, 12px);
            }

            .left-send-offer-auction-shell .auction-bid-amount {
              font-size: 14px;
            }

            .left-send-offer-auction-shell .auction-bid-current-tag {
              font-size: clamp(7px, 0.62vw, 9px);
              padding: 0.0625rem 0.375rem;
            }
          }
        `}
      </style>
      <div className="sticky top-0 z-20 bg-[#F8FAFC] px-4 pb-1 pt-4" dir="rtl">
        <div
          className="nav-tabs flex justify-center gap-4 border-b border-solid border-gray-200 text-base md:gap-2 md:text-[14px]"
          data-testid="left-send-offer-tabs"
        >
          {(viewOnlyAuction
            ? [{ key: "auction", label: "צפייה במכרז" }]
            : [
                { key: "offer", label: "ההצעה שלך" },
                { key: "auction", label: "צפייה במכרז" },
              ]
          ).map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`cursor-pointer border-b px-4 py-2 text-center font-heebo text-[15px] font-medium transition md:px-0 md:py-1 ${
                  isActive
                    ? "border-red-a700_03 text-black"
                    : "border-transparent text-gray-600 hover:text-black"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {auctionRedirecting && (
        <div className="mx-4 rounded-[18px] border border-[#D9E8FF] bg-white px-4 py-3 text-center shadow-[0_10px_26px_rgba(15,23,42,0.08)]" dir="rtl">
          <div className="text-[14px] font-bold text-[#111827]">ההצעה נקלטה בהצלחה</div>
          <div className="mt-1 text-[12px] font-medium text-[#64748B]">
            מעבירים אותך לצפייה במכרז בעוד 3 שניות
          </div>
        </div>
      )}

      {!viewOnlyAuction && (
        <div className={`${activeTab === "offer" ? "flex" : "hidden"} left-send-offer-auction-content`}>
          <div className="w-full flex justify-center">
            <div className="w-full">
              <SendNewOfferForm
                {...commonFormProps}
                onUpdateOffer={handleUpdateOffer}
                auctionBidSnapshot={auctionBidsState}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === "auction" && (
        <div className="left-send-offer-auction-content">
          <AuctionBidsView
            key={`${carId || "missing"}-${currentUserId || "guest"}-${refreshKey}`}
            state={auctionBidsState}
            loading={auctionBidsLoading && !auctionBidsRefreshing}
            error={auctionBidsError}
            showCurrentUserLabel={!viewOnlyAuction}
          />
        </div>
      )}
    </div>
  );
}
